import { RefObject } from "react";
import { Box, Typography } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import { useTranslation } from "react-i18next";
import { Booking } from "src/business/booking";
import {
  colorBorder,
  colorBorderInfo,
  colorChipBackground,
  colorSlotEmptyHover,
  colorSurface,
  colorTextMuted,
  colorTextPrimary,
  radiusCard,
  radiusChip,
} from "src/design/tokens";

interface UnassignedTrayProps {
  bookings: Booking[];
  draggingBookingId: string | null;
  /** True while a booking is being dragged, so the tray can advertise the drop. */
  isDragActive: boolean;
  trayRef: RefObject<HTMLDivElement>;
  onPointerDown: (event: React.PointerEvent, booking: Booking) => void;
  sx?: SxProps;
}

/**
 * Bookings with no table yet. It doubles as a drop zone: dragging a booking back
 * here frees its table without cancelling it, and dragging one out seats it.
 */
export const UnassignedTray = ({
  bookings,
  draggingBookingId,
  isDragActive,
  trayRef,
  onPointerDown,
  sx,
}: UnassignedTrayProps) => {
  const { t } = useTranslation();

  return (
    <Box
      ref={trayRef}
      sx={{
        flexShrink: 0,
        px: { xs: 1, sm: 1.5 },
        py: { xs: 0.5, sm: 1 },
        backgroundColor: colorSurface,
        border: `1px ${isDragActive ? "dashed" : "solid"} ${
          isDragActive ? colorBorderInfo : colorBorder
        }`,
        borderRadius: radiusCard,
        transition: "border-color 120ms ease",
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: 10, sm: 11 },
          lineHeight: 1.4,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: colorTextMuted,
        }}
      >
        {t("tableSchedule.unassigned.title", { count: bookings.length })}
      </Typography>

      {bookings.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: colorTextMuted, mt: 0.5 }}>
          {t("tableSchedule.unassigned.empty")}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.75,
            mt: 0.75,
          }}
        >
          {bookings.map((booking) => (
            <Box
              key={booking.id}
              onPointerDown={(event) => onPointerDown(event, booking)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.4,
                backgroundColor: colorChipBackground,
                border: `1px solid ${colorBorder}`,
                borderRadius: radiusChip,
                cursor: "grab",
                userSelect: "none",
                touchAction: "none",
                opacity: draggingBookingId === booking.id ? 0.4 : 1,
                ":hover": { backgroundColor: colorSlotEmptyHover },
              }}
            >
              <EventSeatIcon sx={{ fontSize: 12, color: colorTextMuted }} />
              <Typography
                sx={{ fontSize: 11.5, fontWeight: 600, color: colorTextPrimary }}
              >
                {booking.userName} · {booking.numberOfSeats} ·{" "}
                {booking.bookingHour}
              </Typography>
            </Box>
          ))}
          <Typography
            sx={{ fontSize: 11, color: colorTextMuted, fontStyle: "italic" }}
          >
            {t("tableSchedule.unassigned.hint")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
